import { useMutation, useQuery } from "@apollo/client";
import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import CloseIcon from "@mui/icons-material/Close";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PhoneEnabledOutlinedIcon from "@mui/icons-material/PhoneEnabledOutlined";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import type { KeyboardEvent } from "react";
import { useCallback, useEffect, useState } from "react";
import { useCanAccess, useNotify, useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import { TELEPHONY_RESOURCE } from "../../constants/resources";
import {
  CrmObject,
  type CrmSearchHit,
  DisconnectPlacetelPhoneDocument,
  PlacetelPhoneAccountDocument,
} from "../../generated/graphql";
import { useCrmLabels } from "../../hooks/useCrmLabels";
import { crmListPath, nationalPhoneForm } from "../../lib/crm";
import { captureException } from "../../lib/errorHandling";
import { isSameCallRecord } from "../../lib/telephony/callRecord";
import type { ActiveCall, DialRecord } from "../../lib/telephony/phoneEngine";
import { space, theme, typography } from "../../theme/designTokens";
import { type CallNote, usePhone, usePhoneNotes } from "./PhoneProvider";
import type { CallerMatches } from "./useCallerMatches";
import { useDialNumber } from "./useDialNumber";
import { useOpenCrmRecord } from "./useOpenCrmRecord";

const ELAPSED_TICK_MS = 1000;
const CALL_NOTE_MAX_LENGTH = 10000;

const formatElapsed = (milliseconds: number): string => {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
};

const ElapsedTime = ({ since }: { since: number }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), ELAPSED_TICK_MS);
    return () => window.clearInterval(timer);
  }, [since]);
  return (
    <Typography data-testid="phone-call-elapsed" sx={{ fontVariantNumeric: "tabular-nums" }}>
      {formatElapsed(now - since)}
    </Typography>
  );
};

/** The caller of a call; the number is empty when the caller withholds it. */
const CallParty = ({ call }: { call: ActiveCall }) => {
  const translate = useTranslate();
  return (
    <>
      <Typography
        data-testid="phone-call-number"
        sx={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, overflowWrap: "anywhere" }}
      >
        {call.number ?? translate("telephony.incoming.unknownNumber")}
      </Typography>
      {call.name && (
        <Typography data-testid="phone-call-name" sx={{ overflowWrap: "anywhere" }}>
          {call.name}
        </Typography>
      )}
    </>
  );
};

type IncomingCallViewProps = {
  call: ActiveCall;
  answering: boolean;
  onAnswer: () => void;
  onDecline: () => void;
};

const IncomingCallView = ({ call, answering, onAnswer, onDecline }: IncomingCallViewProps) => {
  const translate = useTranslate();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.lg}px` }}>
      <Typography data-testid="phone-incoming-title" sx={{ fontWeight: typography.fontWeight.semibold }}>
        {translate("telephony.incoming.title")}
      </Typography>
      <CallParty call={call} />
      <Box sx={{ display: "flex", alignItems: "center", gap: `${space.md}px` }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<CallIcon />}
          onClick={onAnswer}
          disabled={answering}
          data-testid="phone-answer-button"
          sx={{ minHeight: 44 }}
        >
          {translate("telephony.incoming.answer")}
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<CallEndIcon />}
          onClick={onDecline}
          data-testid="phone-decline-button"
          sx={{ minHeight: 44 }}
        >
          {translate("telephony.incoming.decline")}
        </Button>
      </Box>
    </Box>
  );
};

type CallerMatchViewProps = {
  number: string | null;
  callerMatches: CallerMatches;
  onChoose: (hit: CrmSearchHit) => void;
  onRetry: () => void;
};

/** What the CRM knows about the caller of an answered incoming call that is not linked to a record yet. */
const CallerMatchView = ({ number, callerMatches, onChoose, onRetry }: CallerMatchViewProps) => {
  const translate = useTranslate();
  const labels = useCrmLabels();
  const { state, hits } = callerMatches;

  if (state === "several") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.sm}px` }}>
        <Typography data-testid="phone-caller-match-title" sx={{ fontWeight: typography.fontWeight.semibold }}>
          {translate("telephony.callerMatch.title")}
        </Typography>
        {hits.map(hit => (
          <Button
            key={`${hit.object}:${hit.salesforceId}`}
            variant="outlined"
            onClick={() => onChoose(hit)}
            data-testid="phone-caller-match"
            sx={{ minHeight: 44, flexDirection: "column", alignItems: "flex-start", textTransform: "none" }}
          >
            <Typography component="span" sx={{ fontWeight: typography.fontWeight.semibold }}>
              {hit.title}
            </Typography>
            {hit.subtitle && (
              <Typography component="span" sx={{ fontSize: typography.fontSize.sm }}>
                {hit.subtitle}
              </Typography>
            )}
            <Typography component="span" sx={{ fontSize: typography.fontSize.sm }}>
              {labels.object(hit.object)}
            </Typography>
          </Button>
        ))}
      </Box>
    );
  }
  if (state === "error") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.sm}px`, alignItems: "flex-start" }}>
        <Typography data-testid="phone-caller-error">{translate("telephony.callerMatch.error")}</Typography>
        <Button onClick={onRetry} data-testid="phone-caller-retry" sx={{ minHeight: 44 }}>
          {translate("telephony.callerMatch.retry")}
        </Button>
      </Box>
    );
  }
  if (state === "none") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.sm}px` }}>
        <Typography data-testid="phone-caller-none">{translate("telephony.callerMatch.none")}</Typography>
        {number !== null && (
          <Link
            component={RouterLink}
            to={`${crmListPath(CrmObject.Contact)}?q=${encodeURIComponent(nationalPhoneForm(number))}`}
            data-testid="phone-caller-search"
          >
            {translate("telephony.callerMatch.search")}
          </Link>
        )}
      </Box>
    );
  }
  return null;
};

type CallViewProps = {
  call: ActiveCall;
  callerMatches: CallerMatches;
  onChooseMatch: (hit: CrmSearchHit) => void;
  onRetryLookup: () => void;
  /** The record page that is open, when the call can be linked to it. */
  openRecord: DialRecord | null;
  onLinkRecord: (record: DialRecord) => void;
  onMute: (muted: boolean) => void;
  onHangUp: () => void;
};

const CallView = ({
  call,
  callerMatches,
  onChooseMatch,
  onRetryLookup,
  openRecord,
  onLinkRecord,
  onMute,
  onHangUp,
}: CallViewProps) => {
  const translate = useTranslate();
  const connected = call.phase === "connected";
  const inboundMatched = call.direction === "inbound" && connected;
  const { record } = call;
  // The call's own record decides what is shown; the lookup only names it when it is the record it found.
  const matchedRecord =
    inboundMatched && record !== null && callerMatches.linked !== null && isSameCallRecord(record, callerMatches.linked)
      ? callerMatches.linked
      : null;
  const muteLabel = translate(call.muted ? "telephony.call.unmute" : "telephony.call.mute");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.lg}px` }}>
      <CallParty call={call} />
      {inboundMatched && record === null && (
        <CallerMatchView
          number={call.number}
          callerMatches={callerMatches}
          onChoose={onChooseMatch}
          onRetry={onRetryLookup}
        />
      )}
      {matchedRecord && <Typography data-testid="phone-caller-linked">{matchedRecord.title}</Typography>}
      {record !== null && !matchedRecord && (
        <Typography data-testid="phone-call-linked">{translate("telephony.link.linked")}</Typography>
      )}
      {record === null && openRecord && (
        <Button
          variant="outlined"
          onClick={() => onLinkRecord(openRecord)}
          data-testid="phone-link-open-record"
          sx={{ minHeight: 44 }}
        >
          {translate("telephony.link.openRecord")}
        </Button>
      )}
      {connected && <ElapsedTime since={call.startedAt} />}
      {call.phase === "connecting" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: `${space.md}px` }}>
          <CircularProgress size={20} aria-label={translate("telephony.call.connecting")} />
          <Typography data-testid="phone-call-connecting">{translate("telephony.call.connecting")}</Typography>
        </Box>
      )}
      {!connected && call.phase !== "connecting" && (
        <Typography data-testid="phone-call-dialing">{translate("telephony.call.dialing")}</Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: `${space.md}px` }}>
        {connected && (
          <IconButton
            data-testid="phone-mute-button"
            aria-label={muteLabel}
            title={muteLabel}
            aria-pressed={call.muted}
            onClick={() => onMute(!call.muted)}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            {call.muted ? <MicOffOutlinedIcon /> : <MicOutlinedIcon />}
          </IconButton>
        )}
        <Button
          variant="contained"
          color="error"
          startIcon={<CallEndIcon />}
          onClick={onHangUp}
          data-testid="phone-hangup-button"
          sx={{ minHeight: 44 }}
        >
          {translate("telephony.call.hangUp")}
        </Button>
      </Box>
    </Box>
  );
};

type NoteFieldProps = {
  note: CallNote;
  onChange: (text: string) => void;
  onSave: () => void;
};

const NoteField = ({ note, onChange, onSave }: NoteFieldProps) => {
  const translate = useTranslate();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.sm}px` }}>
      <TextField
        label={translate("telephony.note.label")}
        value={note.text}
        onChange={event => onChange(event.target.value)}
        disabled={note.saving}
        multiline
        minRows={2}
        size="small"
        fullWidth
        slotProps={{ htmlInput: { "data-testid": "phone-note-input", maxLength: CALL_NOTE_MAX_LENGTH } }}
      />
      <Button
        variant="outlined"
        onClick={onSave}
        disabled={note.text.trim() === "" || note.saving}
        data-testid="phone-note-save"
        sx={{ minHeight: 44 }}
      >
        {translate("telephony.note.save")}
      </Button>
    </Box>
  );
};

type EndedNoteCardProps = NoteFieldProps & { onDiscard: () => void };

/** The note of a call that ended, kept until the agent saved or closed it. */
const EndedNoteCard = ({ note, onChange, onSave, onDiscard }: EndedNoteCardProps) => {
  const translate = useTranslate();
  return (
    <Box
      data-testid="phone-ended-note"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: `${space.md}px`,
        p: `${space.lg}px`,
        border: `1px solid ${theme.color.panelBorder}`,
        borderRadius: `${theme.borderRadius.md}px`,
      }}
    >
      <Typography sx={{ fontWeight: typography.fontWeight.semibold, overflowWrap: "anywhere" }}>
        {`${translate("telephony.call.ended")} · ${note.number ?? translate("telephony.incoming.unknownNumber")}`}
      </Typography>
      {!note.linked && (
        <Typography data-testid="phone-note-unlinked">{translate("telephony.note.unlinked")}</Typography>
      )}
      <NoteField note={note} onChange={onChange} onSave={onSave} />
      <Button onClick={onDiscard} data-testid="phone-note-discard" sx={{ minHeight: 44 }}>
        {translate("telephony.note.discard")}
      </Button>
    </Box>
  );
};

/** Dial pad of the portal phone: opened from the header, keeps running calls when closed. */
export const PhonePanel = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { canAccess } = useCanAccess({ action: "list", resource: TELEPHONY_RESOURCE });
  const { data } = useQuery(PlacetelPhoneAccountDocument, { skip: !canAccess });
  const [disconnectPhone, { loading: disconnecting }] = useMutation(DisconnectPlacetelPhoneDocument);
  const dial = useDialNumber();
  const {
    status,
    displayStatus,
    retry,
    answer,
    decline,
    hangUp,
    setMuted,
    linkRecord,
    activeCall,
    callerMatches,
    chooseCallerMatch,
    retryCallerLookup,
    panelOpen,
    closePanel,
  } = usePhone();
  const { notes, setNoteText, saveNote, discardNote } = usePhoneNotes();
  const [number, setNumber] = useState("");
  const [dialing, setDialing] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const openRecord = useOpenCrmRecord();

  const account = data?.placetelPhoneAccount;
  const trimmedNumber = number.trim();
  const canDial = status?.kind === "ready" && trimmedNumber !== "" && !activeCall && !dialing;

  const startCall = useCallback(async () => {
    setDialing(true);
    try {
      await dial(trimmedNumber);
    } finally {
      setDialing(false);
    }
  }, [dial, trimmedNumber]);

  const answerCall = useCallback(async () => {
    setAnswering(true);
    try {
      const result = await answer();
      if (result.ok) return;
      if (result.reason === "microphone") notify("telephony.call.microphoneBlocked", { type: "error" });
      else if (result.reason === "failed") notify("telephony.call.failed", { type: "error" });
    } finally {
      setAnswering(false);
    }
  }, [answer, notify]);

  const dialOnEnter = (event: KeyboardEvent) => {
    if (event.key === "Enter" && canDial) void startCall();
  };

  const closeMenu = () => setMenuAnchor(null);

  const retryPhone = () => {
    closeMenu();
    retry();
  };

  const disconnect = async () => {
    closeMenu();
    try {
      await disconnectPhone({ refetchQueries: [PlacetelPhoneAccountDocument] });
      notify("telephony.disconnected", { type: "success" });
    } catch (disconnectError) {
      captureException(disconnectError, "telephony/phone-sign-in: disconnect");
      notify("telephony.disconnectError", { type: "error" });
    }
  };

  if (!canAccess || !panelOpen || !account?.available || !account.connected) return null;

  const ringing = activeCall?.direction === "inbound" && activeCall.phase === "ringing";
  // The note of a call that is not ringing; a call without a backend id has none.
  const activeNote =
    activeCall && !ringing ? notes.find(note => !note.ended && note.callId === activeCall.callId) : undefined;
  const endedNotes = notes.filter(note => note.ended);
  let body;
  if (activeCall && ringing) {
    body = <IncomingCallView call={activeCall} answering={answering} onAnswer={answerCall} onDecline={decline} />;
  } else if (activeCall) {
    body = (
      <CallView
        call={activeCall}
        callerMatches={callerMatches}
        onChooseMatch={chooseCallerMatch}
        onRetryLookup={retryCallerLookup}
        openRecord={openRecord}
        onLinkRecord={linkRecord}
        onMute={setMuted}
        onHangUp={hangUp}
      />
    );
  } else {
    body = (
      <Box sx={{ display: "flex", flexDirection: "column", gap: `${space.lg}px` }}>
        <TextField
          label={translate("telephony.call.numberLabel")}
          value={number}
          onChange={event => setNumber(event.target.value)}
          onKeyDown={dialOnEnter}
          type="tel"
          size="small"
          fullWidth
          slotProps={{ htmlInput: { "data-testid": "phone-number-input", autoComplete: "off" } }}
        />
        <Button
          variant="contained"
          startIcon={<PhoneEnabledOutlinedIcon />}
          onClick={startCall}
          disabled={!canDial}
          data-testid="phone-dial-button"
          sx={{ minHeight: 44 }}
        >
          {translate("telephony.call.dial")}
        </Button>
      </Box>
    );
  }

  return (
    <Paper
      elevation={8}
      role="dialog"
      aria-label={translate("telephony.panel.title")}
      data-testid="phone-panel"
      sx={{
        position: "fixed",
        right: `${space.xl}px`,
        bottom: `${space.xl}px`,
        zIndex: muiTheme => muiTheme.zIndex.drawer + 1,
        width: { xs: `calc(100% - ${2 * space.xl}px)`, sm: 320 },
        maxWidth: 320,
        p: `${space.xl}px`,
        display: "flex",
        flexDirection: "column",
        gap: `${space.lg}px`,
        backgroundColor: theme.color.white,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: `${space.sm}px` }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: typography.fontWeight.semibold }}>
            {translate("telephony.panel.title")}
          </Typography>
          <Typography
            data-testid="phone-status"
            sx={{ fontSize: typography.fontSize.sm, color: theme.color.typographyBase }}
          >
            {translate(`telephony.status.${displayStatus}`)}
          </Typography>
        </Box>
        <IconButton
          data-testid="phone-panel-menu-button"
          aria-label={translate("telephony.panel.menu")}
          title={translate("telephony.panel.menu")}
          aria-haspopup="menu"
          aria-expanded={menuAnchor !== null}
          onClick={event => setMenuAnchor(event.currentTarget)}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <MoreVertIcon />
        </IconButton>
        <IconButton
          data-testid="phone-panel-close"
          aria-label={translate("ra.action.close")}
          title={translate("ra.action.close")}
          onClick={closePanel}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={closeMenu}>
        <MenuItem disabled>{translate("telephony.connectedAs", { email: account.email ?? "" })}</MenuItem>
        {displayStatus === "error" && (
          <MenuItem onClick={retryPhone} data-testid="phone-retry">
            {translate("telephony.retry")}
          </MenuItem>
        )}
        <MenuItem disabled={disconnecting} onClick={disconnect} data-testid="phone-disconnect">
          {translate("telephony.disconnect")}
        </MenuItem>
      </Menu>
      {body}
      {activeNote && (
        <NoteField
          note={activeNote}
          onChange={text => setNoteText(activeNote.callId, text)}
          onSave={() => void saveNote(activeNote.callId)}
        />
      )}
      {endedNotes.map(note => (
        <EndedNoteCard
          key={note.callId}
          note={note}
          onChange={text => setNoteText(note.callId, text)}
          onSave={() => void saveNote(note.callId)}
          onDiscard={() => discardNote(note.callId)}
        />
      ))}
    </Paper>
  );
};
